import React, {useEffect, useRef, useState} from "react";
import {
    XBrowseProps, XFilterElementParams,
    XLazyColumn,
    XLazyDataTable, XLazyDataTableRef,
    XMultilineRenderType
} from "@michalrakus/x-react-web-lib/XLazyDataTable";
import {Utils} from "../../Utils";
import {ZapisForm} from "./ZapisForm";
import {EnumEnum, SluzbaEnum} from "../../common/enums";
import {DataTableFilterMeta, DataTableFilterMetaData} from "primereact/datatable";
import {XButton} from "@michalrakus/x-react-web-lib/XButton";
import {XStorageType, XUtils} from "@michalrakus/x-react-web-lib/XUtils";
import {Zapis} from "../../model/zapisy/zapis.entity";
import {XHtmlRenderer} from "@michalrakus/x-react-web-lib/XHtmlRenderer";
import {
    XFieldSetBase,
    XFieldSetMeta,
    XFieldXFieldMetaMap
} from "@michalrakus/x-react-web-lib/lib/components/XFieldSet/XFieldSetBase";
import {XFieldMeta} from "./XFieldSetBase";
import {XExpansionStateType, XExpansionSwitch} from "./XExpansionSwitch";
import {useXStateSession} from "@michalrakus/x-react-web-lib/useXStateSession";
import {Divider} from "primereact/divider";
import {xLocaleOption} from "@michalrakus/x-react-web-lib/XLocale";
import {KlientSluzba} from "../../model/klient/klient-sluzba.entity";
import {KlientSluzbaColumnFilter} from "../klient/KlientSluzbaColumnFilter";
import {VykazStreetwork} from "../../model/zapisy/vykaz-streetwork.entity";
import {VykazStreetworkAutoComplete} from "./VykazStreetworkAutoComplete";
import {XCustomFilter} from "@michalrakus/x-react-web-lib/FindParam";
import {XUtilsCommon} from "@michalrakus/x-react-web-lib/XUtilsCommon";
import {useXStateStorage} from "@michalrakus/x-react-web-lib/useXStateStorage";

export const ZapisBrowse = (props: XBrowseProps & {vykazStreetwork?: VykazStreetwork}) => {

    // ak sme prisli z VykazStreetworkHlavickaForm, neukladame stav tabulky (filter) do session (nechceme miast uzivatela a chceme vzdy pouzivat vykazStreetwork z props)
    // (vynimka je multiline switch a expansion switch)
    const stateStorage: XStorageType = (props.vykazStreetwork !== undefined ? "none" : "session");

    const sluzbaEnum: SluzbaEnum | undefined = Utils.getCurrentSluzba()?.kod as SluzbaEnum;

    const xLazyDataTableRef = useRef<XLazyDataTableRef>(null);

    const [xFieldSetMap, setXFieldSetMap] = useState<XFieldXFieldMetaMap>(new Map<string, XFieldMeta>);
    const [multilineSwitchValue, setMultilineSwitchValue] = useXStateSession<XMultilineRenderType>(`zapis-browse-${sluzbaEnum}-multiline-switch-value`, "allLines"); // init value

    // osetrovne maju jednoriadkovy popis, nastavime jednoriadkovy rezim, soc porad ma velke texty, nastavime rezim "expanded" (plati len pre desktop)
    const expansionSwitchValueInit: XExpansionStateType = (sluzbaEnum === SluzbaEnum.noclaharen || sluzbaEnum === SluzbaEnum.alzbetaOsetrovna ? "colapsed" : "expanded");
    const [expansionSwitchValue, setExpansionSwitchValue] = useXStateSession<XExpansionStateType>(`zapis-browse-${sluzbaEnum}-expansion-switch-value`, expansionSwitchValueInit); // init value

    const [vykazStreetwork, setVykazStreetwork] = useXStateStorage<VykazStreetwork | null>(stateStorage, `vykaz-streetwork`, props.vykazStreetwork ?? null); // init value

    // parameter [] zabezpeci ze sa metoda zavola len po prvom renderingu (a nie po kazdej zmene stavu (zavolani setNieco()))
    useEffect(() => {
        // ked nemame mobile (mame desktop), tak mame zobrazeny stlpec s klikackami priamo a inicializaciu fieldSetMap vola XLazyDataTable automaticky
        if (XUtils.isMobile()) {
            loadXFieldSetMap();
        }
    },[]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        xLazyDataTableRef.current?.setupExpandedRows();
    }, [expansionSwitchValue]); // Runs after `expansionSwitchValue` changes

    useEffect(() => {
        // refresh browsu sa zavola hned po setnuti vykazu
        xLazyDataTableRef.current?.reread();
    }, [vykazStreetwork]); // Runs after `vykazStreetwork` changes

    const loadXFieldSetMap = async () => {
        // in the future - take from some cache, not from DB
        const xFieldSetMetaList: XFieldSetMeta[] = await XUtils.fetchRows('XFieldSetMeta', {where: "[fieldSetId] = :fieldSetId", params: {fieldSetId: "zapis"}});
        // check
        if (xFieldSetMetaList.length !== 1) {
            throw `FieldSetId "zapis" was not found in DB in the table for Entity XFieldSetMeta`;
        }
        // save created structure
        // pozor! sposobuje dvojnasobne volanie selectov z XLazyDataTable
        setXFieldSetMap(XFieldSetBase.createXFieldXFieldMetaMap(xFieldSetMetaList[0]));
    }


    const showExpandedRowDesktop = (zapis: Zapis, switchValue: XMultilineRenderType): boolean => {
        return expansionSwitchValue === "expanded" && zapis.vseobecnyZapis !== null;
    };

    const rowExpansionTemplateDesktop = (zapis: Zapis) => {
        return (
            <XHtmlRenderer htmlValue={zapis.vseobecnyZapis} renderType={multilineSwitchValue} fewLinesCount={2}/>
        );
    };

    const showExpandedRowMobile = (zapis: Zapis, switchValue: XMultilineRenderType): boolean => {
        return switchValue !== "singleLine" && ((Utils.isSluzbaSocPorad() && zapis.pracovnik1XUser !== null) || (sluzbaEnum === SluzbaEnum.streetwork && zapis.pracovnik2XUser !== null) || zapis.vseobecnyZapis !== null || (zapis.atributy !== null && Object.keys(zapis.atributy).length > 0));
    };

    const rowExpansionTemplateMobile = (zapis: Zapis) => {
        let userNameList: string[] = [];
        if (Utils.isSluzbaSocPorad() && zapis.pracovnik1XUser !== null) {
            userNameList.push(zapis.pracovnik1XUser.name);
        }
        if (sluzbaEnum === SluzbaEnum.streetwork && zapis.pracovnik2XUser !== null) {
            userNameList.push(zapis.pracovnik2XUser.name);
        }
        const mameAtributy: boolean = (zapis.atributy !== null && Object.keys(zapis.atributy).length > 0);
        return (
            <div>
                {userNameList.length > 0 ? <div>{userNameList.join(", ")}</div> : null}
                {userNameList.length > 0 && (mameAtributy || zapis.vseobecnyZapis !== null) ? <Divider className="x-row-expansion-mobile-divider"/> : null}
                {mameAtributy ? <div className="x-multiline-content">{XFieldSetBase.xFieldSetValuesAsUI(zapis.atributy, xFieldSetMap)}</div> : null}
                {mameAtributy && zapis.vseobecnyZapis !== null ? <Divider className="x-row-expansion-mobile-divider"/> : null}
                <XHtmlRenderer htmlValue={zapis.vseobecnyZapis} renderType={multilineSwitchValue} fewLinesCount={2}/>
            </div>
        );
    };

    const onResetTable = () => {
        // vymazeme field na filtrovanie podla denniku
        setVykazStreetwork(null);
        // viac menej kozmeticka zalezitost - ked user klikne na reset filter, tak sa switch vrati do init stavu
        setExpansionSwitchValue(expansionSwitchValueInit);
    }

    const onAddRow = async (filters: DataTableFilterMeta) => {

        if (!Utils.getCurrentSluzba()) {
            alert("Vyberte najprv službu.");
            return;
        }

        // if (Utils.getCurrentSluzba()?.kod === SluzbaEnum.streetwork) {
        //     alert("Zápis pre streetwork je dovolené vytvoriť len cez voľbu Zápisy - streetwork.");
        //     return;
        // }

        let initValues: object = {};

        if (sluzbaEnum === SluzbaEnum.streetwork) {
            if (vykazStreetwork) {
                // rereadneme z DB aby sme mali najnovsie data (pozri poznamku v Utils.klientSluzbaFromFilterReread)
                // zoznam fieldov ma byt ten isty (dolezite su assoc) ako v autocomplete vykazStreetwork v ZapisForm
                const vykazStreetworkFromFilterFromDB: VykazStreetwork = await XUtils.fetchById("VykazStreetwork", ["datum", "vykazXUserList.xUser.name"], vykazStreetwork.id);
                // drobne vylepsenie - pracovnika predplnime (na aktualneho usera) len vtedy, ak je medzi pracovnikmi daneho dennika (ak nie tak zostava null)
                initValues = {vykazStreetwork: vykazStreetworkFromFilterFromDB, datum: vykazStreetworkFromFilterFromDB?.datum, pracovnik1XUser: Utils.streetworkZapisPracovnikForInit(vykazStreetworkFromFilterFromDB)};
            }
            else {
                // este nemame dennik, pracovnika nepredplnime (na aktualneho usera), predplnime ho az po zadani dennika
                // (aby sme nemuseli riesit dilemu ci zmazat predplneneho pracovnika po zadani dennika)
                initValues = {pracovnik1XUser: null};
            }
        }

        const klientSluzbaFromFilterFromDB: KlientSluzba | null = await Utils.klientSluzbaFromFilterReread(filters["klientSluzba.klient.menoPriezviskoPrezyvka"] as DataTableFilterMetaData, false, Utils.isSluzbaOsetrovna());
        initValues = {...initValues, klientSluzba: klientSluzbaFromFilterFromDB};

        // openForm pridavame automaticky v XFormNavigator3 pri renderovani komponentu
        props.openForm!(<ZapisForm initValues={initValues} onSaveOrCancel={XUtils.onSaveOrCancelNavigator(props.openForm!, xLazyDataTableRef)}/>);
    }

    const onEdit = (selectedRow: any) => {

        // openForm pridavame automaticky v XFormNavigator3 pri renderovani komponentu
        props.openForm!(<ZapisForm id={selectedRow.id} onSaveOrCancel={XUtils.onSaveOrCancelNavigator(props.openForm!, xLazyDataTableRef)}/>);
    }

    const onRemoveRow = async (selectedRow: any): Promise<boolean> =>  {

        if (window.confirm(xLocaleOption('removeRowConfirm'))) {
            await XUtils.post('remove-zapis', {id: selectedRow.id});
            return true;
        }
        return false;
    }

    const rowClassNameDesktop = (row: Zapis) => {
        if (expansionSwitchValue === 'expanded' && row?.klientSluzba?.klient?.menoPriezviskoPrezyvka) {
            return 'zapis-hlavny-riadok';
        }
        else {
            return '';
        }
    };

    const rowClassNameMobile = (row: Zapis) => {
        // na mobile pouzivame len multiline switch
        if (multilineSwitchValue !== 'singleLine' && row?.klientSluzba?.klient?.menoPriezviskoPrezyvka) {
            return 'zapis-hlavny-riadok';
        }
        else {
            return '';
        }
    };

    const renderDesktop = (customFilter: XCustomFilter | undefined) => {

        // ak sme v expanded rezime, potrebujeme pridat medzi full text stlpce aj stlpec "vseobecnyZapis" (ma viewStatus = false)
        // preto vytvarame zoznam stlpcov pre full text search explicitne (manualne)
        const fullTextSearchColumns: string[] = ["datum", "klientSluzba.klient.menoPriezviskoPrezyvka", "vseobecnyZapis", "atributy"];
        if (Utils.isSluzbaSocPorad()) {
            fullTextSearchColumns.push("pracovnik1XUser.name");
        }
        if (sluzbaEnum === SluzbaEnum.streetwork) {
            fullTextSearchColumns.push("pracovnik2XUser.name");
        }
        if (sluzbaEnum === SluzbaEnum.lujzaPoradenstvo) {
            fullTextSearchColumns.push("lujzaPoradProjekt.name");
        }
        if (sluzbaEnum === SluzbaEnum.vincentPoradenstvo) {
            fullTextSearchColumns.push("vincentPoradProjekt.name");
        }

        // niektoremu zo stlpcov Zapis/Klikacky nastavime restWidth = availWidth - <ostatne stlpce>, aby nam nevznikal horizontalny scrollbar na malych displayoch
        // width="calc(100vw - <ostatne stlpce>)" nefunguje, XLazyDataTable alebo DataTable nam to meni na calc(100vw), pouzijeme window.screen.availWidth
        // mozno sa to da aj nejako sikovnejsie cez nejaky fit-content alebo primereact atribut...
        let otherColumnsWidth: number = 0;
        const idColumnWidth: string = "5rem";
        if (Utils.isUserAdmin()) {
            otherColumnsWidth += XUtils.toPX0(idColumnWidth); // ID
        }
        if (sluzbaEnum !== SluzbaEnum.streetwork || vykazStreetwork === null) {
            otherColumnsWidth += XUtils.toPX0("7.25rem"); // Dátum
        }
        // poznamka: ked bol len stlpec pracovnici, mal width 12rem
        const pracovnik1ColumnWidth: string = "10rem";
        if (Utils.isSluzbaSocPorad()) {
            otherColumnsWidth += XUtils.toPX0(pracovnik1ColumnWidth); // Pracovnik 1
        }
        const pracovnik2ColumnWidth: string = "10rem";
        if (sluzbaEnum === SluzbaEnum.streetwork) {
            otherColumnsWidth += XUtils.toPX0(pracovnik2ColumnWidth); // Pracovnik 2
        }
        const klientColumnWidth: string = (expansionSwitchValue === "colapsed" ? "12rem" : "17rem");
        otherColumnsWidth += XUtils.toPX0(klientColumnWidth); // Klient
        const atributyColumnWidth: string = "20rem";
        if (expansionSwitchValue === "colapsed") {
            otherColumnsWidth += XUtils.toPX0(atributyColumnWidth); // Klikacky
        }
        const lujzaProjektColumnWidth: string = "9rem";
        if (sluzbaEnum === SluzbaEnum.lujzaPoradenstvo) {
            otherColumnsWidth += XUtils.toPX0(lujzaProjektColumnWidth); // Projekt
        }
        const vincentProjektColumnWidth: string = "10rem";
        if (sluzbaEnum === SluzbaEnum.vincentPoradenstvo) {
            otherColumnsWidth += XUtils.toPX0(vincentProjektColumnWidth); // Projekt
        }

        // zohladnime nejake min/max, ak je display prilis uzky/siroky
        let viewWidth: number = XUtils.getViewWidth();
        const maxViewWidth: number = XUtils.toPX0("120rem");
        if (viewWidth > maxViewWidth) {
            viewWidth = maxViewWidth; // tabulka nebude natiahnuta na celu sirku display-a
        }

        let restWidth: number = viewWidth - otherColumnsWidth - XUtils.toPX0("2.5rem"); // 2.5 su margins + vertikalny scrollbar

        const minColumnWidth: number = XUtils.toPX0("15rem");
        if (restWidth < minColumnWidth) {
            restWidth = minColumnWidth; // bude horizontalny scrollbar
        }

        const restColumnWidth: string = `${restWidth}px`;

        return (
            <div>
                <XLazyDataTable ref={xLazyDataTableRef} entity="Zapis" stateStorage={stateStorage} stateKey={`zapis-browse-${sluzbaEnum}`}
                                label={Utils.isSluzbaNoclaharen() ? "Ošetrenia" : "Zápisy"} sortField={[{field: "datum", order: -1}, {field: "id", order: -1}]} rows={30}
                                showExpandedRow={showExpandedRowDesktop} rowExpansionTemplate={rowExpansionTemplateDesktop}
                                customFilter={customFilter}
                                fullTextSearch={fullTextSearchColumns}
                                onResetTable={onResetTable} onAddRow={onAddRow} onEdit={onEdit} removeRow={onRemoveRow}
                                appButtons={props.vykazStreetwork ? <XButton key="naspet" label="Naspäť" onClick={() => (props as any).openForm(null)}/> : undefined} /* ak sme prisli z VykazStreetworkHlavickaForm, zobrazime button Naspet */
                                rowClassName={rowClassNameDesktop}
                                multilineSwitch={true} multilineSwitchValue={[multilineSwitchValue, setMultilineSwitchValue]}
                                headerBodyLeft={sluzbaEnum === SluzbaEnum.streetwork ?
                                    [<div className="m-1">Denník</div>,
                                     <VykazStreetworkAutoComplete value={vykazStreetwork} onChange={setVykazStreetwork} maxWidth="30rem"
                                                                     error={undefined} onErrorChange={(error: (string | undefined)) => {}}/>] : undefined}
                                headerBodyRight={<XExpansionSwitch value={expansionSwitchValue} onChange={(switchValue: XExpansionStateType) => {setExpansionSwitchValue(switchValue); /*setupExpandedRows(value, switchValue);*/}} className="m-1"/>}>
                    <XLazyColumn field="id" header="ID" width={idColumnWidth} columnViewStatus={Utils.isUserAdmin()}/>
                    <XLazyColumn field="datum" header="Dátum" betweenFilter="column" columnViewStatus={sluzbaEnum !== SluzbaEnum.streetwork || vykazStreetwork === null}/>
                    <XLazyColumn field="pracovnik1XUser.name" header={sluzbaEnum === SluzbaEnum.streetwork ? "Pracovník 1" : "Pracovník"} width={pracovnik1ColumnWidth}
                                 autoComplete={Utils.userColumnAutoComplete("pracovnik1XUser")} autoFilter={true}
                                 columnViewStatus={Utils.isSluzbaSocPorad()}/>
                    <XLazyColumn field="pracovnik2XUser.name" header="Pracovník 2" width={pracovnik2ColumnWidth}
                                 autoComplete={Utils.userColumnAutoComplete("pracovnik2XUser")} autoFilter={true}
                                 columnViewStatus={sluzbaEnum === SluzbaEnum.streetwork}/>
                    <XLazyColumn field="klientSluzba.klient.menoPriezviskoPrezyvka" header="Klient" width={klientColumnWidth} className="klient-meno"
                                 filterElement={{element: (params: XFilterElementParams) => <KlientSluzbaColumnFilter filterParams={params} dropdownButtonEnabled={false}/>, matchModeLabel: xLocaleOption('xAutoComplete')}}/>
                    <XLazyColumn field="vseobecnyZapis" header="Zápis" width={restColumnWidth} contentType="html" columnViewStatus={expansionSwitchValue === "colapsed"}/>
                    <XLazyColumn field="atributy" fieldSetId="zapis" header="Klikačky" width={expansionSwitchValue === "colapsed" ? atributyColumnWidth : restColumnWidth}/>
                    {/*<XLazyColumn field="zapisFieldList.xFieldSetMetaFieldMap.label" header="Klikačky" width={expansionSwitchValue === "colapsed" ? atributyColumnWidth : restColumnWidth}/>*/}
                    <XLazyColumn field="lujzaPoradProjekt.name" header="Projekt" width={lujzaProjektColumnWidth} columnViewStatus={sluzbaEnum === SluzbaEnum.lujzaPoradenstvo} dropdownInFilter={true} dropdownFilter={Utils.enumFilter(EnumEnum.lujzaPoradProjekt)}/>
                    <XLazyColumn field="vincentPoradProjekt.name" header="Projekt" width={vincentProjektColumnWidth} columnViewStatus={sluzbaEnum === SluzbaEnum.vincentPoradenstvo} dropdownInFilter={true} dropdownFilter={Utils.enumFilter(EnumEnum.vincentPoradProjekt)}/>
                </XLazyDataTable>
            </div>
        );
    }

    const renderMobile = (customFilter: XCustomFilter | undefined) => {

        const fields: string[] = [];
        if (Utils.isSluzbaSocPorad()) {
            fields.push("pracovnik1XUser.name");
        }
        if (sluzbaEnum === SluzbaEnum.streetwork) {
            fields.push("pracovnik2XUser.name");
        }

        // potrebujeme pridat medzi full text stlpce aj stlpce zobrazovane v expanded casti
        // preto vytvarame zoznam stlpcov pre full text search explicitne (manualne)
        const fullTextSearchColumns: string[] = ["datum", "klientSluzba.klient.menoPriezviskoPrezyvka"];
        if (multilineSwitchValue !== "singleLine") {
            if (Utils.isSluzbaSocPorad()) {
                fullTextSearchColumns.push("pracovnik1XUser.name");
            }
            if (sluzbaEnum === SluzbaEnum.streetwork) {
                fullTextSearchColumns.push("pracovnik2XUser.name");
            }
            fullTextSearchColumns.push("vseobecnyZapis");
            fullTextSearchColumns.push("atributy");
        }

        // width="calc(100vw - 1rem)" nefunguje, XLazyDataTable alebo DataTable nam to meni na calc(100vw)
        const tableWidth: number = XUtils.getViewWidth() - XUtils.toPX0("1rem"); // 1rem - sirka okrajov (element body - padding)
        const fullTextSearchWidth: number = tableWidth - 302; // experimentalne zistena sirka "zvysku"

        let columnKlientWidth: number = tableWidth;
        if (sluzbaEnum !== SluzbaEnum.streetwork || vykazStreetwork === null) {
            columnKlientWidth -= XUtils.toPX0("7.25rem"); // zobrazujeme aj stlpec Dátum
        }

        return (
            <div>
                <XLazyDataTable ref={xLazyDataTableRef} entity="Zapis" stateStorage={stateStorage} stateKey={`zapis-browse-${sluzbaEnum}`}
                                label={Utils.isSluzbaNoclaharen() ? "Ošetr..." : "Zápisy"} labelStyle={{marginRight: '0rem'}} fullTextSearchWidth={`${fullTextSearchWidth}px`}
                                sortField={[{field: "datum", order: -1}, {field: "id", order: -1}]} rows={30}
                                fields={fields}
                                showExpandedRow={showExpandedRowMobile} rowExpansionTemplate={rowExpansionTemplateMobile}
                                customFilter={customFilter}
                                fullTextSearch={fullTextSearchColumns}
                                onResetTable={onResetTable} onAddRow={onAddRow} onEdit={onEdit} removeRow={true}
                                appButtons={props.vykazStreetwork ? <XButton key="naspet" label="Naspäť" onClick={() => (props as any).openForm(null)}/> : undefined} /* ak sme prisli z VykazStreetworkHlavickaForm, zobrazime button Naspet */
                                rowClassName={rowClassNameMobile}
                                multilineSwitch={true} multilineSwitchValue={[multilineSwitchValue, setMultilineSwitchValue]}
                                headerBodyTop={sluzbaEnum === SluzbaEnum.streetwork ?
                                    [<div className="m-1">Denník</div>,
                                     <VykazStreetworkAutoComplete value={vykazStreetwork} onChange={setVykazStreetwork} maxWidth="30rem"
                                                                  error={undefined} onErrorChange={(error: (string | undefined)) => {}}/>] : undefined}
                                formFooterHeight={sluzbaEnum === SluzbaEnum.streetwork ? '3rem' : undefined} /* height autocomplete Dennik */>
                    <XLazyColumn field="datum" header="Dátum" width="7.25rem" autoFilter={true} columnViewStatus={sluzbaEnum !== SluzbaEnum.streetwork || vykazStreetwork === null}/>
                    <XLazyColumn field="klientSluzba.klient.menoPriezviskoPrezyvka" header="Klient" width={`${columnKlientWidth}px`} className="klient-meno"
                                 filterElement={{element: (params: XFilterElementParams) => <KlientSluzbaColumnFilter filterParams={params} dropdownButtonEnabled={false}/>, matchModeLabel: xLocaleOption('xAutoComplete')}}/>
                </XLazyDataTable>
            </div>
        );
    }

    let customFilter: XCustomFilter | undefined = Utils.filterCurrentSluzba();
    if (sluzbaEnum === SluzbaEnum.streetwork && vykazStreetwork !== null) {
        customFilter = XUtilsCommon.filterAnd(customFilter, {where: `[vykazStreetwork] = ${vykazStreetwork.id}`, params: {}});
    }

    if (!XUtils.isMobile()) {
        return renderDesktop(customFilter);
    }
    else {
        return renderMobile(customFilter);
    }
}
